import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';

import SectionEyebrow from '../LandingSections/SectionEyebrow';
import MultiBoardBox from '../../common/components/MultiBoardBox';
import MultiBoardLessonListTable from '../../pages/EditClass/MultiBoardLessonListTable';
import store from '../../redux/store';
import { showMultiBoardLessonState } from '../../redux/actions';
import { PILL_SECONDARY, TEXT_LINK } from '../../pages/Home/HomeDashboard/dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    paper: {
        boxSizing: 'border-box',
        width: 'min(1100px, calc(100vw - 32px))',
        maxWidth: 'none',
        maxHeight: 'calc(100vh - 32px)',
        margin: '16px',
        padding: 'clamp(20px, 3vw, 32px)',
        borderRadius: '28px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
        color: REDESIGN_COLORS.dark,
    },
    head: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '16px',
        marginBottom: '18px',
    },
    eyebrowRow: {
        marginBottom: '12px',
    },
    title: {
        margin: 0,
        fontSize: 'clamp(1.3rem, 2.4vw, 1.8rem)',
        lineHeight: 1.15,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    back: {
        ...TEXT_LINK,
        marginTop: '10px',
        padding: 0,
        border: 0,
        background: 'none',
        fontFamily: REDESIGN_FONTS.body,
        cursor: 'pointer',
    },
    close: {
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        width: '40px',
        height: '40px',
        padding: 0,
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '50%',
        color: REDESIGN_COLORS.dark,
        background: REDESIGN_COLORS.white,
        cursor: 'pointer',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            color: REDESIGN_COLORS.link,
        },
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
    },
    foot: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '18px',
    },
    dismiss: {
        ...PILL_SECONDARY,
        minHeight: '44px',
    },
}));

/**
 * The individual-boards activity of a group: the list of saved sessions,
 * and, once one is picked, that session's boards per participant. Opened from
 * the groups page, the lesson menu and the group's edit page.
 */
const MultiBoardDialog = ({ appState }) => {
    const classes = useStyles();
    const intl = useIntl();
    const state = appState.multiBoardLessonState;
    const open = !!state;
    const viewingSession = !!(state && state.id);
    const canGoBack = viewingSession && state.lessonId && state.email;

    const close = () => store.dispatch(showMultiBoardLessonState(null));
    const back = () =>
        store.dispatch(
            showMultiBoardLessonState({
                id: null,
                lessonId: state.lessonId,
                name: state.name,
                email: state.email,
                isOwner: state.isOwner,
            })
        );

    return (
        <Dialog open={open} onClose={close} classes={{ paper: classes.paper }} aria-labelledby="activity-title">
            <div className={classes.head}>
                <div>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id="redesign.updateClass.activity" />
                        </SectionEyebrow>
                    </div>
                    <h2 id="activity-title" className={classes.title}>
                        <FormattedMessage id="menu.pastLessons" />
                    </h2>
                    {canGoBack ? (
                        <button type="button" onClick={back} className={classes.back}>
                            <span aria-hidden="true">←</span>
                            <FormattedMessage id="home.firstGroup.back" />
                        </button>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={close}
                    aria-label={intl.formatMessage({ id: 'close' })}
                    className={classes.close}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                </button>
            </div>

            <div className={classes.body}>
                {!open ? null : !viewingSession && state.lessonId && state.email ? (
                    <MultiBoardLessonListTable
                        lessonId={state.lessonId}
                        lessonName={state.name}
                        profileEmail={state.email}
                        displayTitle={false}
                        isOwner={state.isOwner}
                    />
                ) : (
                    <MultiBoardBox />
                )}
            </div>

            <div className={classes.foot}>
                <button type="button" onClick={close} className={classes.dismiss}>
                    <FormattedMessage id="close" />
                </button>
            </div>
        </Dialog>
    );
};

const mapStateToProps = (state) => {
    const { appState } = state;
    return { appState };
};

export default connect(mapStateToProps)(MultiBoardDialog);
